import { CardGridSkeleton, PageShellSkeleton } from '@/components/ui/skeletons'
import { getResourceForPath, isUnderBasePath } from '@/data/nav-data'
import { useAccessControl } from '@/hooks/access-control'
import { useDisplayPreference } from '@/hooks/useDisplayPreference'
import { stripScopePrefix } from '@/lib/routeScope'

/**
 * The skeleton a route shows while its chunk and its first fetch are in flight.
 *
 * Shapes are keyed by module, so the skeleton resembles the page that is coming rather than a generic
 * block: a settings page gets cards, a list gets a table with the right column count.
 */
const ROUTE_SKELETON_SHAPES: Record<
	string,
	{
		checkbox?: boolean
		pager?: boolean
		display?: boolean
		cols?: number
		toolbar?: boolean
		body?: 'table' | 'settings'
		cards?: number
	}
> = {
	'/settings/audit-logs': { checkbox: false },
	'/sessions/dashboard': { checkbox: false },
	'/system-notifications': { checkbox: false, display: false },
	'/settings/diagnostics': { checkbox: false, pager: false, display: false },
	'/meters': { checkbox: false, pager: false, display: false },
	'/actions/remote-commands': { pager: false },
	'/operations': { cols: 7 },
	'/actions/ota-campaigns': { pager: false },
	'/settings/streams': { cols: 2, toolbar: false },
	'/settings/metadata': { cols: 2, display: false },
	'/settings/device-state': { cols: 5, display: false },
	'/settings/users': { cols: 3, display: false },
	'/settings/roles': { cols: 2, display: false },
	'/settings/subscriber-groups': { display: false },
	'/settings/api-keys': { cols: 6, display: false },
	'/data/exporters': { cols: 6 },
	'/actions/assets': { cols: 6, display: false },
	'/device-management': { toolbar: false },
	'/inventory': { toolbar: false, cols: 3 },
	'/settings/general': { body: 'settings', cards: 4 },
	'/settings/modules': { body: 'settings', cards: 2 },
	'/settings/quotas': { body: 'settings', cards: 1 },
}

const REMOTE_COMMANDS_ROUTES = ['/actions/remote-commands', '/operations']

/**
 * Shape entry governing a page, matched on the module the URL names.
 *
 * Prefix matched rather than searched for anywhere in the path: a project called `inventory` or
 * `meters` would otherwise take the shape of the organization module it shares a name with.
 */
export function getSkeletonShapeForSection(section: string) {
	return Object.entries(ROUTE_SKELETON_SHAPES).find(([route]) => isUnderBasePath(section, route))?.[1]
}

export function RoutePendingSkeleton() {
	const section = stripScopePrefix(window.location.pathname)
	const shape = getSkeletonShapeForSection(section)
	const [remoteCommandsGroupBy] = useDisplayPreference<string | null>('remoteCommands', 'groupBy', 'status')
	const routeResource = getResourceForPath(section)
	const routeAccess = useAccessControl({
		resource: routeResource as Parameters<typeof useAccessControl>[0]['resource'],
		action: 'view',
	})

	// This route lands on the restricted state, not a list. Skipping the skeleton avoids flashing a
	// toolbar and table where the header no longer is.
	if (routeResource !== undefined && routeAccess.state === 'denied') {
		return null
	}

	if (shape?.body === 'settings') {
		return (
			<CardGridSkeleton
				count={shape.cards ?? 3}
				columns={1}
				lines={3}
				className='w-full max-w-4xl mx-auto px-4 mt-4 min-h-0 flex-1 auto-rows-fr'
			/>
		)
	}
	const hasPager = REMOTE_COMMANDS_ROUTES.some(route => isUnderBasePath(section, route))
		? remoteCommandsGroupBy === null
		: (shape?.pager ?? true)
	return (
		<PageShellSkeleton
			withHeader={false}
			withToolbar={shape?.toolbar ?? true}
			withCheckbox={shape?.checkbox ?? true}
			withPager={hasPager}
			withDisplay={shape?.display ?? true}
			cols={shape?.cols}
		/>
	)
}
